import ModeOutlinedIcon from "@mui/icons-material/ModeOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { Box, Grid, IconButton } from "@mui/material";
import { ReactNode } from "react";

import Flex from "../../../../../components/Flex";
import OutlineCard from "../../../../../components/OutlineCard";
import PdfViewDrawer from "../../../../../components/PdfViewDrawer";
import ProfileViewer from "../../../../../components/ProfileViewer";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import { GENDER_RECORD } from "../../../../../constants";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { useGetReferralById } from "../../../../../queries/collaboration/referral/use-get-referral-by-uuid";
// import { useReferralPrintById } from "../../../../../queries/collaboration/referral/use-get-referral-print-by-uuid";
import { formatDate } from "../../../../../utils/date-time-utility";
import {
  DEFAULT_VALUE_LABEL_LIST,
  getAddressFormat,
  // getFormattedId,
  // getIdFromObj,
  // getValueFromObj,
  getXtenantId,
  // replaceUnderScoreAddSpace,
  transformMultiSelectFrontEndVal,
} from "../../../../../utils/utils";
// import { isEmpty } from "lodash";
import { getObjectFromList } from "../collaboration-referral-constant";
import PrintReferral from "./PrintReferral";
import UpsertReferral from "./UpsertReferral";

const SectionCard = ({
  title,
  children,
  fullHeight,
}: {
  title: string;
  children: ReactNode;
  fullHeight?: boolean;
}) => (
  <OutlineCard
    sx={{
      padding: "16px",
      width: "100%",
      ...(fullHeight && { height: "100%" }),
    }}
  >
    <Text variant="h4" fontWeight={"medium"} mb={1.5}>
      {title}
    </Text>
    {children}
  </OutlineCard>
);

const ViewReferral = ({
  disclosureHost,
  title = "Referral Summary",
}: {
  disclosureHost: DisclosureHost;
  title?: string;
}) => {
  const [previewResultDisc, printReferralDisclosure, referralDisclosureHost] =
    useReduceDisclosure(3);

  const { data } = useGetReferralById({
    referralId: disclosureHost?.data,
    xTENANTID: getXtenantId() as string,
  });

  const referralData = data?.data;

  // const { mutateAsync: printReferral } = useReferralPrintById();

  // const getReferralPrint = async () => {
  //   printReferral({
  //     referralId: disclosureHost?.data,
  //     xTENANTID: getXtenantId() as string,
  //   }).then((res) => {
  //     const blob = new Blob([res?.data], { type: "application/pdf" });
  //     const fileUrl = URL.createObjectURL(blob);
  //     printViewDisclosure.open(fileUrl);
  //   });
  // };

  const referralFromSpeciality = referralData?.referralFromSpecialities
    ? Object.values(referralData?.referralFromSpecialities).join(", ")
    : "-";

  const referralToSpeciality = referralData?.referralToSpecialities
    ? Object.values(referralData?.referralToSpecialities).join(", ")
    : "-";

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={disclosureHost.close}
      title={title || "Referral Summary"}
      w={"1200px"}
      endIcon={
        <Flex gap={1}>
          <IconButton
            onClick={() => referralDisclosureHost.open(referralData?.uuid)}
          >
            <ModeOutlinedIcon color="primary" />
          </IconButton>
          <IconButton
            onClick={() => printReferralDisclosure.open(referralData?.uuid)}
          >
            <PrintOutlinedIcon color="primary" />
          </IconButton>
        </Flex>
      }
    >
      <PdfViewDrawer
        w="50vw"
        file={referralData?.referralDocument}
        disclosureHost={previewResultDisc}
        title="Attachment"
      />
      <PrintReferral disclosureHost={printReferralDisclosure} />
      <UpsertReferral
        title="Update Referral"
        disclosureHost={referralDisclosureHost}
        prefillData={{
          ...referralData,
          patient: getObjectFromList(referralData?.patient),
          referralFrom: getObjectFromList(referralData?.referralFrom),
          referralTo: getObjectFromList(referralData?.referralTo),
          referralSpecialities: getObjectFromList(
            referralData?.referralSpecialities
          ),
          referralDiagnosisCode: transformMultiSelectFrontEndVal(
            DEFAULT_VALUE_LABEL_LIST,
            referralData?.referralDiagnosisCode
          ),
          referralDocument: referralData?.referralDocument ?? undefined,
          referralDocumentName: referralData?.referralDocumentName ?? undefined,
          patientInsurance: referralData?.patientInsurance
            ? {
                value: referralData?.patientInsurance?.uuid,
                label:
                  referralData?.patientInsurance?.insurancePayer?.payerName,
              }
            : undefined,
          referralAppointment: getObjectFromList(
            referralData?.referralAppointment
          ),
        }}
      />

      {referralData && (
        <Flex flexDirection={"column"} gap={"16px"} width={"100%"}>
          {/* Top row: Patient | Referral From | Referral To */}
          <Grid container spacing={2} alignItems={"stretch"}>
            <Grid item xs={12} md={4}>
              <SectionCard title="Patient" fullHeight>
                <ProfileViewer
                  gap={"10px"}
                  firstColW={4}
                  secondColW={8}
                  data={{
                    Name: referralData?.patientName || "-",
                    DOB: formatDate(referralData?.patientDob) || "-",
                    Gender: GENDER_RECORD[referralData?.patientGender] || "-",
                    Address:
                      getAddressFormat(referralData?.patientAddress) || "-",
                  }}
                />
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <SectionCard title="Referral From" fullHeight>
                <ProfileViewer
                  gap={"10px"}
                  firstColW={4}
                  secondColW={8}
                  data={{
                    Name: referralData?.referralFromName || "-",
                    Speciality: referralFromSpeciality,
                    Address:
                      getAddressFormat(referralData?.referralFromAddress) ||
                      "-",
                    Phone: referralData?.referralFromPhone || "-",
                    Fax: referralData?.referralFromFax || "-",
                  }}
                />
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <SectionCard title="Referral To" fullHeight>
                <ProfileViewer
                  gap={"10px"}
                  firstColW={4}
                  secondColW={8}
                  data={{
                    Name: referralData?.referralToName || "-",
                    Speciality: referralToSpeciality,
                    Address:
                      getAddressFormat(referralData?.referralToAddress) || "-",
                    Phone: referralData?.referralToPhone || "-",
                    Fax: referralData?.referralToFax || "-",
                  }}
                />
              </SectionCard>
            </Grid>
          </Grid>

          {/* Referral Date */}
          <SectionCard title="Referral Date">
            <ProfileViewer
              gap={"10px"}
              firstColW={2}
              secondColW={10}
              data={{
                Date: formatDate(referralData?.referralDate) || "-",
              }}
            />
          </SectionCard>

          {/* Referral details: Reason / Priority / Status / Encounter / Appointment */}
          {/* <SectionCard title="Referral Details">
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Text fontWeight={"medium"} fontSize={"13px"} mb={0.5}>
                  Referral Reason
                </Text>
                <Text fontSize={"14px"}>
                  {referralData?.referralReason || "-"}
                </Text>
              </Grid>
              <Grid item xs={12} md={4}>
                <Text fontWeight={"medium"} fontSize={"13px"} mb={0.5}>
                  Priority
                </Text>
                <Text fontSize={"14px"}>
                  {referralData?.referralPriority
                    ? replaceUnderScoreAddSpace(
                        referralData.referralPriority.toLowerCase()
                      )
                    : "-"}
                </Text>
              </Grid>
              <Grid item xs={12} md={4}>
                <Text fontWeight={"medium"} fontSize={"13px"} mb={0.5}>
                  Referral Status
                </Text>
                <Text fontSize={"14px"}>
                  {referralData?.referralStatus
                    ? replaceUnderScoreAddSpace(
                        referralData.referralStatus.toLowerCase()
                      )
                    : "-"}
                </Text>
              </Grid>
              <Grid item xs={12} md={6}>
                <Text fontWeight={"medium"} fontSize={"13px"} mb={0.5}>
                  Encounter
                </Text>
                <Text fontSize={"14px"}>{referralData?.encounter || "-"}</Text>
              </Grid>
              <Grid item xs={12} md={6}>
                <Text fontWeight={"medium"} fontSize={"13px"} mb={0.5}>
                  Appointment
                </Text>
                <Text fontSize={"14px"}>
                  {!isEmpty(referralData?.referralAppointment)
                    ? `${getFormattedId(
                        getIdFromObj(referralData?.referralAppointment)
                      )} - ${getValueFromObj(
                        referralData?.referralAppointment
                      )}`
                    : "-"}
                </Text>
              </Grid>
            </Grid>
          </SectionCard> */}

          {/* Diagnoses + Insurance */}
          {/* <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SectionCard title="Diagnoses">
                {referralData?.referralDiagnosisCode &&
                Object.values(referralData.referralDiagnosisCode).length > 0 ? (
                  <Flex flexDirection={"column"} gap={"6px"}>
                    {Object.values(referralData.referralDiagnosisCode).map(
                      (diagnosis, index) => (
                        <Text key={index} fontSize={"14px"}>
                          {index + 1}. {diagnosis as string}
                        </Text>
                      )
                    )}
                  </Flex>
                ) : (
                  <Text fontSize={"14px"}>-</Text>
                )}
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionCard title="Insurance">
                <ProfileViewer
                  gap={"10px"}
                  firstColW={4}
                  secondColW={8}
                  data={{
                    Name: referralData?.insuranceName || "-",
                    "Member ID": referralData?.insuranceMemberId || "-",
                    "Expiry Date": referralData?.insuranceExpiryDate || "-",
                    Phone: referralData?.insurancePhone || "-",
                  }}
                />
              </SectionCard>
            </Grid>
          </Grid> */}

          {/* Referral Note */}
          <SectionCard title="Referral Note">
            <Text fontSize={"14px"}>{referralData?.referralNote || "-"}</Text>
          </SectionCard>

          {/* Attachments */}
          <SectionCard title="Attachments">
            {referralData?.referralDocument ? (
              <Box>
                <Text fontSize={"14px"}>
                  {`1. `}
                  <a
                    href={referralData?.referralDocument}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {referralData?.referralDocumentName || "Attachment"}
                  </a>
                </Text>
              </Box>
            ) : (
              <Text fontSize={"14px"}>-</Text>
            )}
          </SectionCard>
        </Flex>
      )}
    </SideSheetDrawer>
  );
};

export default ViewReferral;
